/**
 * API Utility Functions
 * Handles token management and API calls with authentication
 */

const isLocalHost = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost'
  || window.location.hostname === '127.0.0.1'
  || window.location.hostname === '[::1]'
);
const API_BASE_URL = process.env.REACT_APP_API_URL
  || (isLocalHost ? 'http://localhost:8080' : 'https://api.moviq.live');

console.log("API_BASE_URL", API_BASE_URL);


const ACCESS_TOKEN_KEYS = ['token', 'accessToken', 'authToken'];

/**
 * Access token only. Never returns the refresh token.
 * @returns {string|null}
 */
export const getAuthToken = () => {
  for (const key of ACCESS_TOKEN_KEYS) {
    const value = localStorage.getItem(key);
    if (value) {
      return value;
    }
  }
  return null;
};

/**
 * Persist the access token as token, accessToken, and authToken.
 * @param {string} token
 */
export const saveAuthToken = (token) => {
  if (!token) {
    return;
  }
  ACCESS_TOKEN_KEYS.forEach((key) => localStorage.setItem(key, token));
};

export const removeAuthToken = () => {
  ACCESS_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const getRefreshToken = () => {
  return localStorage.getItem('refreshToken');
};

export const saveRefreshToken = (token) => {
  if (token) {
    localStorage.setItem('refreshToken', token);
  }
};

export const removeRefreshToken = () => {
  localStorage.removeItem('refreshToken');
};

/**
 * Save the access token and refresh token from verify or login.
 * Refresh token is stored separately and is never used as a Bearer token.
 */
export const saveAuthSession = (session = {}) => {
  const accessToken = session.token || session.accessToken;
  if (accessToken) {
    saveAuthToken(accessToken);
  }
  if (session.refreshToken) {
    saveRefreshToken(session.refreshToken);
  }
};

const syncStoredAccessToken = (accessToken, refreshToken) => {
  saveAuthToken(accessToken);
  if (refreshToken) {
    saveRefreshToken(refreshToken);
  }
  try {
    const guest = JSON.parse(localStorage.getItem('guestData') || '{}');
    if (guest?.userData?.data) {
      guest.userData.data.token = accessToken;
      guest.userData.data.accessToken = accessToken;
      localStorage.setItem('guestData', JSON.stringify(guest));
    }
  } catch (error) {
    console.error('Unable to update the stored session token', error);
  }
};

/**
 * Clear tokens and the signed-in user.
 */
export const clearUserSession = () => {
  removeAuthToken();
  removeRefreshToken();
  localStorage.removeItem('guestData');
  localStorage.removeItem('selectedHotel');
  localStorage.removeItem('hasVisited');
  localStorage.removeItem('firebaseUser');
};

/**
 * Handle token expiration - clears session and triggers redirect
 */
export const handleTokenExpiration = () => {
  clearUserSession();
  // Dispatch custom event to notify App.js
  window.dispatchEvent(new CustomEvent('tokenExpired'));
};

/**
 * Get headers for API requests with authentication token
 * @param {Object} additionalHeaders - Additional headers to include
 * @returns {Object} Headers object with Authorization header if token exists
 */
export const getAuthHeaders = (additionalHeaders = {}) => {
  const token = getAuthToken();
  const headers = {
    ...additionalHeaders
  };

  // Add ngrok bypass header if using ngrok URL
  if (API_BASE_URL && API_BASE_URL.includes('ngrok-free.dev')) {
    headers['ngrok-skip-browser-warning'] = 'true';
  }


  // Only add Content-Type for requests with body (POST, PUT, PATCH)
  if (additionalHeaders['Content-Type'] !== undefined) {
    headers['Content-Type'] = additionalHeaders['Content-Type'];
  } else {
    // Default to application/json if not specified
    headers['Content-Type'] = 'application/json';
  }

  // Always add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

const REFRESH_TOKEN_PATH = '/auth/refresh-token';
let refreshInFlight = null;

/**
 * Ask the API for a new access token using the saved refresh token.
 * POST /auth/refresh-token  { refreshToken }
 * Expects { data: { token, refreshToken } }.
 */
const authExpired = (message) => {
  const error = new Error(message || 'Token expired. Please login again.');
  error.code = 'AUTH_EXPIRED';
  return error;
};

export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw authExpired('No refresh token');
  }

  const headers = { 'Content-Type': 'application/json' };
  if (API_BASE_URL && API_BASE_URL.includes('ngrok-free.dev')) {
    headers['ngrok-skip-browser-warning'] = 'true';
  }

  const response = await fetch(`${API_BASE_URL}${REFRESH_TOKEN_PATH}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ refreshToken })
  });
  const data = await parseResponse(response);
  const session = data?.data && typeof data.data === 'object' ? data.data : null;
  const accessToken = session?.token || session?.accessToken;
  const nextRefreshToken = session?.refreshToken;

  if (response.status === 401 || data?.status === false || !accessToken || !nextRefreshToken) {
    throw authExpired(data?.message || 'Unable to refresh token');
  }

  if (!response.ok) {
    throw new Error(data?.message || 'Unable to refresh token');
  }

  syncStoredAccessToken(accessToken, nextRefreshToken);
  return accessToken;
};

const refreshAccessTokenOnce = () => {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/**
 * Make an authenticated API request.
 * A 401 refreshes the access token once and retries the same request.
 * @param {string} url - The API endpoint (relative or absolute)
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>} The fetch response
 */
export const apiRequest = async (url, options = {}) => {
  const { skipAuth = false, _retried = false, ...fetchOptions } = options;
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;

  const authHeaders = skipAuth
    ? { 'Content-Type': 'application/json', ...(fetchOptions.headers || {}) }
    : getAuthHeaders(fetchOptions.headers || {});

  const finalHeaders = skipAuth
    ? authHeaders
    : {
        ...(fetchOptions.headers || {}),
        ...authHeaders
      };

  if (isFormData) {
    delete finalHeaders['Content-Type'];
  }

  const response = await fetch(fullUrl, {
    ...fetchOptions,
    headers: finalHeaders
  });

  const isRefreshCall = fullUrl.includes(REFRESH_TOKEN_PATH);
  if (!skipAuth && !isRefreshCall && response.status === 401) {
    if (_retried) {
      handleTokenExpiration();
      throw new Error('Token expired. Please login again.');
    }
    try {
      await refreshAccessTokenOnce();
      return apiRequest(url, { ...options, _retried: true });
    } catch (error) {
      if (error?.code === 'AUTH_EXPIRED') {
        handleTokenExpiration();
        throw new Error('Token expired. Please login again.');
      }
      throw error;
    }
  }

  return response;
};

/**
 * Parse API response (handles both JSON and text responses)
 * @param {Response} response - The fetch response
 * @returns {Promise<Object|Array|string>} Parsed response data
 */
export const parseResponse = async (response) => {
  const text = await response.text();
  if (!text) {
    return {};
  }
  
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
};

/**
 * Normalize nearby-hotels API payloads (raw array, or { data: [] }, etc.)
 * @returns {Array|null} Array of hotels, or null if the payload cannot be interpreted
 */
export const getLoggedInUserId = () => {
  try {
    const guest = JSON.parse(localStorage.getItem('guestData') || '{}');
    const nested = guest?.userData?.data || guest?.userData || {};
    const id = nested.id || nested.userId || nested.customerId || guest.id || guest.userId || guest.customerId;
    if (id == null || id === '') {
      return null;
    }
    const numeric = Number(id);
    return Number.isFinite(numeric) ? numeric : id;
  } catch (error) {
    return null;
  }
};

export const toApiPaymentMethod = (method) => {
  const raw = String(method || '').trim().toUpperCase();
  if (raw === 'CASH' || raw.includes('CASH')) {
    return 'CASH';
  }
  return 'ONLINE';
};

export const bookingIdOf = (booking) => {
  if (!booking || typeof booking !== 'object') {
    return null;
  }
  return booking.bookingId ?? booking.id ?? null;
};

export const unwrapBooking = (parsed) => {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return parsed;
  }
  if (parsed.bookingId != null) {
    return parsed;
  }
  if (parsed.data && typeof parsed.data === 'object' && !Array.isArray(parsed.data) && parsed.data.bookingId != null) {
    return parsed.data;
  }
  return parsed;
};

export const unwrapRideGroups = (parsed) => {
  const source = parsed && (Array.isArray(parsed.created) || Array.isArray(parsed.confirmed))
    ? parsed
    : (parsed?.data && (Array.isArray(parsed.data.created) || Array.isArray(parsed.data.confirmed))
      ? parsed.data
      : parsed);
  return {
    created: Array.isArray(source?.created) ? source.created : [],
    vendorAssigned: Array.isArray(source?.vendorAssigned) ? source.vendorAssigned : [],
    carAssigned: Array.isArray(source?.carAssigned) ? source.carAssigned : [],
    confirmed: Array.isArray(source?.confirmed) ? source.confirmed : [],
    cancelled: Array.isArray(source?.cancelled) ? source.cancelled : [],
    completed: Array.isArray(source?.completed) ? source.completed : []
  };
};

export const apiErrorMessage = (parsed, fallback) => {
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.message) {
    return String(parsed.message);
  }
  if (typeof parsed === 'string' && parsed.trim()) {
    return parsed.length > 180 ? fallback : parsed;
  }
  return fallback;
};

export const extractHotelsList = (parsed) => {
  if (parsed == null) {
    return [];
  }
  if (Array.isArray(parsed)) {
    return parsed;
  }
  if (typeof parsed === 'object') {
    if (Array.isArray(parsed.data)) {
      return parsed.data;
    }
    if (Array.isArray(parsed.hotels)) {
      return parsed.hotels;
    }
    if (Array.isArray(parsed.results)) {
      return parsed.results;
    }
  }
  return null;
};
